import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";

/**
 * Phase-aware status-copy rotator for long waits (generation, thinking,
 * bootstrap). A single frozen "Generating…" reads as a hang; rotating the copy
 * every few seconds signals liveness without faking progress.
 *
 * Honors `prefers-reduced-motion`: under reduced motion it holds `messages[0]`
 * and never churns. Resets to the first message whenever the `messages` array
 * identity changes (a phase switch), so callers pass a fresh array per phase.
 *
 * Pass a single-element array (e.g. a truthful "page X of Y" line) to pin one
 * message with no rotation.
 */
export function useRotatingStatus(messages: string[], intervalMs = 2600): string {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);

  // Reset to the first message when the phase (array identity) changes.
  useEffect(() => setIndex(0), [messages]);

  useEffect(() => {
    if (reduce || messages.length <= 1) return;
    const id = window.setInterval(
      () => setIndex((n) => (n + 1) % messages.length),
      intervalMs,
    );
    return () => window.clearInterval(id);
  }, [reduce, messages, intervalMs]);

  return messages[Math.min(index, messages.length - 1)] ?? "";
}
